import { z } from 'zod';
import { ValidationError } from '../errors/index.js';
import { brandedUuid } from '../FlavorSchema.js';
import { AuditContextSchema } from './ApiSchemas.js';
import { DateTimeSchema } from './DateTimeSchemas.js';
export const BusinessMetricIdSchema = brandedUuid('Invalid business metric ID format');
// ============================================================================
// Metric Name Validation
// ============================================================================
/**
 * Metric name format: {team}.{metric}.{submetric}
 * Each part: lowercase alphanumeric with hyphens allowed
 * Examples: platform.free-cash-flow.total, billing.revenue.monthly
 */
const METRIC_NAME_REGEX = /^[a-z0-9-]+\.[a-z0-9-]+\.[a-z0-9-]+$/;
export const MetricNameSchema = z
    .string()
    .min(5, 'Metric name too short')
    .max(255, 'Metric name too long')
    .regex(METRIC_NAME_REGEX, 'Metric name must be in format: team.metric.submetric (lowercase alphanumeric with hyphens)');
/**
 * Parses a metric name into its component parts.
 * @throws ValidationError if the metric name doesn't match the expected format
 */
export function parseMetricName(metricName) {
    const result = MetricNameSchema.safeParse(metricName);
    if (!result.success) {
        throw new ValidationError(`Invalid metric name format: ${metricName}. Expected format: team.metric.submetric (lowercase alphanumeric with hyphens)`, 'metricName');
    }
    const [team, metric, submetric] = metricName.split('.');
    return { team, metric, submetric };
}
// ============================================================================
// JSON Value Schema
// ============================================================================
/**
 * Schema for validating JSON primitive values (string, number, boolean, null)
 */
export const JsonPrimitiveSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
/**
 * Schema for validating any valid JSON value (primitive, object, or array).
 * Uses z.lazy() for recursive type definition.
 */
export const JsonValueSchema = z.lazy(() => z.union([JsonPrimitiveSchema, z.array(JsonValueSchema), z.record(z.string(), JsonValueSchema)]));
// ============================================================================
// Segments Schemas
// ============================================================================
/**
 * Segment values can be strings, numbers, or booleans.
 * Used for dimensional filtering of metrics.
 */
export const MetricSegmentValueSchema = z.union([z.string(), z.number(), z.boolean()]);
/**
 * Segments are key-value pairs for dimensional filtering.
 * Example: { businessContext: "citrine", currency: "GBP", granularity: "month" }
 */
export const MetricSegmentsSchema = z.record(z.string(), MetricSegmentValueSchema).default({});
// ============================================================================
// Request Schemas
// ============================================================================
/**
 * Core fields for upserting a business metric
 */
export const UpsertBusinessMetricDataSchema = z.object({
    /** Metric name in format: team.metric.submetric */
    metricName: MetricNameSchema,
    /** Timestamp for the metric data point (ISO 8601 format) */
    timestamp: DateTimeSchema,
    /** Segments for dimensional filtering (defaults to {}) */
    segments: MetricSegmentsSchema,
    /** The metric value - can be any JSON-compatible value */
    value: JsonValueSchema,
});
/**
 * Request body for upserting a business metric
 */
export const UpsertBusinessMetricRequestSchema = z.object({
    request: UpsertBusinessMetricDataSchema,
    context: AuditContextSchema,
});
/**
 * Core fields for deleting a business metric
 */
export const DeleteBusinessMetricDataSchema = z.object({
    /** Metric name in format: team.metric.submetric */
    metricName: MetricNameSchema,
    /** Timestamp for the metric data point (ISO 8601 format) */
    timestamp: DateTimeSchema,
    /** Segments must match exactly (defaults to {}) */
    segments: MetricSegmentsSchema,
});
/**
 * Request body for deleting a business metric
 */
export const DeleteBusinessMetricRequestSchema = z.object({
    request: DeleteBusinessMetricDataSchema,
    context: AuditContextSchema,
});
// ============================================================================
// Response Schemas
// ============================================================================
/**
 * Response for a single business metric
 */
export const BusinessMetricResponseSchema = z.object({
    id: BusinessMetricIdSchema,
    metricName: z.string(),
    team: z.string(),
    metric: z.string(),
    submetric: z.string(),
    timestamp: DateTimeSchema,
    segments: MetricSegmentsSchema,
    value: JsonValueSchema,
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
});
//# sourceMappingURL=BusinessMetricSchemas.js.map